import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Dimmer, Icon, Loader } from 'semantic-ui-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import { Timeline, TimelineEvent } from 'react-event-timeline';
import { getMessages, sendMessage, updateViewed } from '../../services/followup';
import Button from '../Button';
import { get } from '../../services/storage';

import { MessageContainer, MessageBlockContainer, MessageBlockTitle } from './styles';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../services/notification';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import styled from 'styled-components';
import { hasPermission } from '../AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { set } from 'lodash';
import { FaFire } from 'react-icons/fa';

export type MessageType = {
  id?: string;
  text: string;
  senderId: {
    name:
    | boolean
    | React.ReactChild
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
    role: string;
  };
  createdAt: moment.MomentInput;
};

const Div = styled.div`
  strong {
    font-weight: bold;
  }
`;

const FollowUp = (props: {
  messages: MessageType[];
  id: string;
  page: number;
  totalMessages: number;
  isBlocked?: boolean;
  setPage: (page: number) => void;
  onClaimFinalized?: () => void;
}) => {
  const [isContentLoading, setIsContentLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(props.messages);
  const [message, setMessage] = useState('');
  const id = props.id;
  const claim_id = id;
  const user_id = get('uid');
  const userName = get('name');
  const userEmail = get('email');
  const userRole = get('role');

  const [userCanSendMessage, setUserCanSendMessage] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const { page, setPage } = props;

  const perPage = 50;
  const totalPages = Math.ceil(props.totalMessages! / perPage);

  const handleScroll = async () => {
    const el = scrollRef.current;
    if (!el || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMore && !isContentLoading) {
      try {
        setIsContentLoading(true);
        const currentPage = page || 1;
        const nextPage = currentPage + 1;
        setPage(nextPage);
        const response = await getMessages(id, nextPage);
        const { data } = response;
        setMessages(prevMessages => [...prevMessages, ...data]);
      } catch (error) {
        console.error('Erro ao carregar mais mensagens:', error);
        dangerNotification(
          'Oops...',
          'Não foi possível carregar mais mensagens, tente novamente mais tarde.'
        );
      } finally {
        setIsContentLoading(false);
      }
    }
  };

  const MessageBlocked = () => {
    return (
      <MessageBlockContainer>
        <MessageBlockTitle>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '3px'
            }}
          >
            <FaFire />
          </div>
          Sinistro finalizado
        </MessageBlockTitle>
        <div>
          O sinistro já está finalizado e não é possível enviar novas mensagens, mas você
          ainda pode consultar os registros anteriores.
        </div>
        <div>
          Se precisar de ajuda, entre em contato com LadoBom em
          contato@ladobomseguros.com.br
        </div>
      </MessageBlockContainer>
    );
  };

  useEffect(() => {
    const has = hasPermission(ActionEnum.create, FeatureEnum.followups);
    setUserCanSendMessage(has);
    if (messages && messages.length > 0) {
      setHasMore(totalPages > 1);
    }
  }, []);

  useEffect(() => {
    setHasMore(totalPages > page);
  }, [page]);

  useEffect(() => {
    const notSeen = messages.filter((message: any) => !message.viewed);
    let willUpdate = [];
    if (userRole?.includes('ESTATE')) {
      willUpdate = notSeen.filter(
        (message: any) =>
          ![UserRolesEnum.estateAdmin, UserRolesEnum.estateUser].includes(
            message.senderId.role
          )
      );
    } else {
      // const claimUsersIds = [
      //   'a4c0ba47-fa8d-47a8-a2f5-f4c1844a9b2e', // Fernanda Medeiros
      //   '3afa044e-cf3b-4ed2-b3f6-435200de8313', // Juliana Loch
      //   '5e697f16-983d-4259-81fe-89195c97ee32', // Julia Maria Rodrigues
      //   'be905980-c1d2-4497-8ac9-178e673cef96', // Bruna Araujo
      //   '6a83e5de-cde2-42fd-968b-dc3d66260a64', // Roque Junior
      //   '5ee05efc-54f7-4349-9ed5-916d6581b5cd', // Fernanda Roling
      //   '2959602b-ca18-47ea-b2cd-f01b54c6856d', // Roque
      //   'e1ab81be-e2fa-449a-9418-179956677e0e' // Renan Vidal
      // ];

      const estateMessages = notSeen.filter(
        (message: any) =>
          message.senderId?.role === UserRolesEnum.estateAdmin ||
          message.senderId?.role === UserRolesEnum.estateUser
      );

      /// apenas o resposnsável, ou, os usuários que estão na lista de usuários da reivindicação
      willUpdate = estateMessages.filter((message: any) => {
        if (user_id === message.claimEstateId.responsibleId) {
          return true;
        } else {
          ///return claimUsersIds.includes(user_id!);
          return userEmail?.includes('@imobseguros.com.br');
        }
      });
    }

    willUpdate.length > 0 &&
      successNotification('Lido', `${willUpdate.length} mensagens marcadas como lidas!`);
    for (const message of willUpdate) {
      if (message.id && user_id) {
        updateViewed(message.id, user_id);
      }
    }
  }, [messages]);

  const handleSubmit = async () => {
    if (!message.trim() || !message || message.length < 1) {
      warningNotification('Oops...', 'Por favor, digite uma mensagem antes de enviar.');
    } else {
      try {
        setLoading(true);
        await sendMessage(claim_id, user_id!, message, userName!);
        const response = await getMessages(id, 1);
        const { data, count } = response;
        setMessages(data || []);
        setHasMore(count > perPage);
        setPage(1);
        successNotification('Sucesso!', 'Mensagem enviada!');
      } catch (error) {
        console.log('ERRO AO ENVIAR MENSAGEM:', error);

        const err = error as { statusCode?: number; message?: string };
        if (
          err.statusCode === 403 &&
          err.message ===
          'Esse sinistro foi finalizado, Se precisar de ajuda, entre em contato com LadoBom em contato@ladobomseguros.com.br'
        ) {
          warningNotification('Mensagem não salva', err.message);
          if (props.onClaimFinalized) {
            props.onClaimFinalized();
          }
          return;
        }

        dangerNotification(
          'Oops...',
          'Não foi possível enviar a mensagem, tente novamente mais tarde.'
        );
      } finally {
        setMessage('');
        setLoading(false);
      }
    }
  };

  const modules = {
    toolbar: [
      [{ align: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['normal', 'large', 'huge'] }],
      [{ color: [] }, { background: [] }]
    ]
  };

  return (
    <Div>
      {props.isBlocked ? (
        <MessageBlocked />
      ) : (
        <MessageContainer>
          <div className="quilljs">
            <ReactQuill
              theme="snow"
              modules={modules}
              value={message}
              onChange={value => setMessage(value)}
              readOnly={loading || !userCanSendMessage || props.isBlocked}
            />
          </div>
          <div>
            <Button
              primary
              content="Enviar"
              labelPosition="right"
              icon="send"
              onClick={handleSubmit}
              loading={loading}
              disabled={loading || !userCanSendMessage || props.isBlocked}
            />
          </div>
        </MessageContainer>
      )}

      <div
        style={{ overflowY: 'scroll', maxHeight: '50vh' }}
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {isContentLoading && (
          <Dimmer active={isContentLoading} inverted>
            <Loader content="Carregando mensagens..." />
          </Dimmer>
        )}

        <Timeline>
          {messages.length &&
            messages.map((message: MessageType) => {
              return (
                <TimelineEvent
                  key={message.id}
                  title={message.senderId.name ? message.senderId.name : userName}
                  createdAt={moment(message.createdAt).format('DD/MM/YY - HH:mm')}
                  icon={<Icon name="envelope open outline" size="large" />}
                  iconColor="#5C6BC0"
                  style={{
                    backgroundColor: '#fff',
                    padding: 10,
                    boxShadow: '0 0 3px 1px #5C6BC0'
                  }}
                  iconStyle={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 2
                  }}
                  contentStyle={{
                    backgroundColor: '#FEFEFE',
                    color: '#5C6BC0'
                  }}
                >
                  <ReactQuill theme="bubble" value={message.text} readOnly={true} />
                </TimelineEvent>
              );
            })}
        </Timeline>
      </div>
    </Div>
  );
};

export default FollowUp;

{
  /* <Item.Group>
      {messages.map((message: MessageType) => {
        return (
          <Item>
            <Item.Content>
              <Item.Header>{message.text}</Item.Header>
              <ContainerInfo>
                <Item.Meta><span>{moment(message.createdAt).format('DD/MM/YY - HH:mm:ss')}</span></Item.Meta>
                <Item.Description><p>{message.senderId.name}</p></Item.Description>
              </ContainerInfo>
            </Item.Content>
          </Item>
        )
      })}
    </Item.Group> */
}
